/**
 * SweetAlert2 wrappers with KRCL brand colors.
 *
 * Two helpers:
 *   - confirmDestructive(opts) — red-themed confirm for irreversible actions
 *     (returns true if the user confirms).
 *   - askForReason(opts) — textarea modal with required validation
 *     (returns the trimmed string or null if cancelled).
 *
 * Brand colors are pulled from the Tailwind v4 @theme tokens at runtime
 * via getComputedStyle so dark mode / theme tweaks Just Work.
 */
import Swal, { type SweetAlertResult } from "sweetalert2";

function brandColor(varName: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return value || fallback;
}

const RED = () => brandColor("--color-flag-red", "#ED1C24");
const STONE = () => brandColor("--color-stone-400", "#a8a29e");

/** Escape a string for safe insertion into a SweetAlert `html` payload. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export async function confirmDestructive(opts: {
  title: string;
  text?: string;
  confirmText?: string;
  cancelText?: string;
}): Promise<boolean> {
  // Render via `html` so newlines + lists in `text` survive, but escape the
  // input first so it stays a safe string-render (no rich HTML injection
  // from callers passing user-controlled values).
  const html = opts.text
    ? `<div style="text-align:left; white-space:pre-wrap; line-height:1.45;">${escapeHtml(opts.text)}</div>`
    : undefined;

  const result: SweetAlertResult = await Swal.fire({
    title: opts.title,
    html,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: opts.confirmText ?? "Yes, delete",
    cancelButtonText: opts.cancelText ?? "Cancel",
    confirmButtonColor: RED(),
    cancelButtonColor: STONE(),
    reverseButtons: true,
    focusCancel: true,
  });
  return Boolean(result.isConfirmed);
}

export async function askForReason(opts: {
  title: string;
  text?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
}): Promise<string | null> {
  const result: SweetAlertResult<string> = await Swal.fire({
    title: opts.title,
    text: opts.text,
    input: "textarea",
    inputPlaceholder: opts.placeholder,
    inputValidator: (value) => {
      if (!value || !value.trim()) {
        return "A reason is required (it goes into the audit log).";
      }
      return null;
    },
    showCancelButton: true,
    confirmButtonText: opts.confirmText ?? "Confirm",
    cancelButtonText: opts.cancelText ?? "Cancel",
    confirmButtonColor: RED(),
    cancelButtonColor: STONE(),
    reverseButtons: true,
  });
  if (!result.isConfirmed) return null;
  return (result.value ?? "").trim() || null;
}

export async function showError(opts: { title: string; text?: string }): Promise<void> {
  await Swal.fire({
    title: opts.title,
    text: opts.text,
    icon: "error",
    confirmButtonColor: RED(),
  });
}

export async function showSuccess(opts: { title: string; text?: string }): Promise<void> {
  await Swal.fire({
    title: opts.title,
    text: opts.text,
    icon: "success",
    confirmButtonColor: RED(),
    timer: 1500,
    showConfirmButton: false,
  });
}
